import { useState } from 'react';

import { Close, Save } from '@mui/icons-material';
import { Box, IconButton, MenuItem, TableCell, TableRow, TextField } from '@mui/material';

import { EnumModel, EnumPropertyModel } from '../../../api/api';
import { getRequiredFieldProps, usePropertySaveValidation } from './usePropertySaveValidation';

interface Props {
	onSave: (property: EnumPropertyModel) => Promise<void> | void;
	onIsEditingChanged: (isEditing: boolean) => void;
	availableEnums: EnumModel[];
	name: string;
	onNameChange: (value: string) => void;
	description: string;
	onDescriptionChange: (value: string) => void;
	isRequired: boolean;
	onIsRequiredChange: (value: boolean) => void;
	isMultiple: boolean;
	onIsMultipleChange: (value: boolean) => void;
}

const AddEnumPropertyRow: React.FC<Props> = ({
	onSave,
	onIsEditingChanged,
	availableEnums,
	name,
	onNameChange,
	description,
	onDescriptionChange,
	isRequired,
	onIsRequiredChange,
	isMultiple,
	onIsMultipleChange,
}) => {
	const [selectedEnum, setSelectedEnum] = useState<EnumModel | null>(null);
	const [enumId, setEnumId] = useState('');
	const [enumContextId, setEnumContextId] = useState('');
	const [defaultValue, setDefaultValue] = useState('');

	const isNameValid = name.trim().length > 0;
	const isTypeValid = !!enumId;
	const canSave = isNameValid && isTypeValid;
	const { saveAttempted, validateForSave, resetValidation } = usePropertySaveValidation(canSave);

	const handleSave = async () => {
		if (!validateForSave()) {
			return;
		}

		try {
			await onSave({ propertyName: name, propertyDescription: description, isRequired, isMultiple, enumId, enumContextId, defaultValue });
			setSelectedEnum(null);
			setEnumId('');
			setEnumContextId('');
			setDefaultValue('');
			resetValidation();
			onIsEditingChanged(false);
		} catch (err) {
			console.error('Failed to save property', err);
		}
	};

	return (
		<>
			<TableRow>
				<TableCell>
					<TextField
						variant="outlined"
						size="small"
						fullWidth
						{...getRequiredFieldProps(saveAttempted && !isNameValid)}
						value={name}
						onChange={(e) => {
							const sanitized = e.target.value.replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-_]/g, '');
							onNameChange(sanitized);
						}}
					/>
				</TableCell>
				<TableCell>
					<>
						<TextField
							select
							variant="outlined"
							size="small"
							fullWidth
							{...getRequiredFieldProps(saveAttempted && !isTypeValid)}
							value={enumId}
							onChange={(e) => {
								const enumSelected = availableEnums.find((enumModel) => enumModel.id === e.target.value);
								setSelectedEnum(enumSelected ?? null);
								setEnumId(e.target.value);
								setEnumContextId(enumSelected?.contextId ?? '');
							}}
						>
							{availableEnums.map((option) => (
								<MenuItem key={option.name} value={option.id}>
									{option.name}
								</MenuItem>
							))}
						</TextField>
					</>
				</TableCell>
				<TableCell>
					<Box display="flex" justifyContent="left">
						<input type="checkbox" checked={isRequired} onChange={(e) => onIsRequiredChange(e.target.checked)} />
					</Box>
				</TableCell>
				<TableCell>
					<Box display="flex" justifyContent="left">
						<input type="checkbox" checked={isMultiple} onChange={(e) => onIsMultipleChange(e.target.checked)} />
					</Box>
				</TableCell>
				<TableCell colSpan={1}>
					<Box display="flex" alignItems="center">
						<TextField select variant="outlined" size="small" fullWidth value={defaultValue} onChange={(e) => setDefaultValue(e.target.value)}>
							{selectedEnum?.items.map((option) => (
								<MenuItem key={option.name} value={option.id.toString()}>
									{option.name}
								</MenuItem>
							))}
						</TextField>
					</Box>
				</TableCell>
				<TableCell>
					<Box display="flex" justifyContent="right">
						<IconButton onClick={handleSave} size="small" color="primary">
							<Save />
						</IconButton>
						<IconButton onClick={() => onIsEditingChanged(false)} size="small">
							<Close />
						</IconButton>
					</Box>
				</TableCell>
			</TableRow>
			<TableRow>
				<TableCell colSpan={6}>
					<TextField
						label="Description"
						variant="outlined"
						size="small"
						fullWidth
						multiline
						value={description}
						onChange={(e) => onDescriptionChange(e.target.value)}
					/>
				</TableCell>
			</TableRow>
		</>
	);
};

export default AddEnumPropertyRow;
